import type { CandidateOutreachSendMethod } from 'clients/CampaignsClient/CampaignsClient.types';
import type { UserResponse } from 'clients/UserClient/types';
import type { default as Candidate } from 'model/Candidate/Candidate';
import type { CandidateHistory, CandidateHistoryQueryData } from '../useCandidateHistoryQuery.types';

import { queryClient } from 'config/queryClient';
import { CandidateHistoryAction } from 'services/candidates/types';
import { extractLastRefresh } from 'shared/legacy/contexts/CandidateContext/searchv3/extractLastRefresh';
import { QueryKey } from 'types/query';

export const candidateHistoryQuerySetValue = ({
  candidate,
  campaignName,
  sendMethod,
  currentUser,
  language,
  content,
  metadata = {},
}: {
  candidate: Candidate;
  sendMethod: CandidateOutreachSendMethod;
  currentUser: UserResponse | undefined;
  campaignName: string | undefined;
  language: string;
  content: CandidateHistory['content'];
  metadata?: Record<string, any>;
}) => {
  const queryKey = [
    QueryKey.useCandidateHistoryQuery,
    { candidateEsPersonId: candidate.es_person_id, status: candidate.status },
  ];

  const existingData = queryClient.getQueryData<CandidateHistoryQueryData>(queryKey);
  if (!existingData || existingData.data.length === 0) return;

  queryClient.setQueryData<CandidateHistoryQueryData>(queryKey, (d) => {
    if (!d) return d;

    let action: CandidateHistoryAction;

    switch (sendMethod) {
      case 'email':
        action = CandidateHistoryAction.EmailSent;
        break;
      case 'linkedin_inmail':
        action = CandidateHistoryAction.InMailSent;
        break;
      case 'linkedin':
        action = CandidateHistoryAction.LinkedinSent;
        break;
      case 'indeed':
        action = CandidateHistoryAction.IndeedSent;
        break;
      default:
        action = CandidateHistoryAction.Contacted;
    }

    const newHistoryItem: CandidateHistory = {
      action,
      content,
      by: { id: currentUser?.id ?? -1, name: currentUser?.first_name ?? '$name' },
      campaign: { id: -1, name: campaignName ?? '$campaign' },
      createdAt: new Date(),
      id: Date.now(),
      timeAgo: extractLastRefresh(new Date(), language),
      user: { id: currentUser?.id ?? -1 },
      metadata,
      message: {
        isNew: false,
        source: sendMethod,
      },
      messageReceived: [
        CandidateHistoryAction.EmailReceived,
        CandidateHistoryAction.InMailReceived,
        CandidateHistoryAction.IndeedReceived,
        CandidateHistoryAction.LinkedinReceived,
      ].includes(action),
    };

    return {
      ...d,
      data: [...d.data, newHistoryItem],
    };
  });
};

export const addCalendarInvitationSentToCandidateHistoryQueryData = ({
  candidate,
  currentUser,
  campaignName,
  language,
}: {
  candidate: Candidate;
  currentUser: UserResponse | undefined;
  campaignName: string | undefined;
  language: string;
}) => {
  const queryKey = [
    QueryKey.useCandidateHistoryQuery,
    { candidateEsPersonId: candidate.es_person_id, status: candidate.status },
  ];

  const existingData = queryClient.getQueryData<CandidateHistoryQueryData>(queryKey);
  if (!existingData || existingData.data.length === 0) return;

  queryClient.setQueryData<CandidateHistoryQueryData>(queryKey, (d) => {
    if (!d) return d;
    const entry: CandidateHistory = {
      action: CandidateHistoryAction.CalendarInterviewInvitationSent,
      id: Date.now() + 1,
      createdAt: new Date(),
      timeAgo: extractLastRefresh(new Date(), language),
      campaign: { id: -1, name: campaignName ?? '$campaign' },
      by: { id: currentUser?.id ?? -1, name: currentUser?.first_name ?? '$name' },
      user: { id: currentUser?.id ?? -1 },
      messageReceived: false,
    };
    return { ...d, data: [...d.data, entry] };
  });
};
